import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem, Chip, Alert,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";

import { fetchJob, updateJob, fetchJobUsed } from "../../api/companyApi";
import { fetchRoles } from "../../api/marketApi";
import { useNotification } from "../../providers/NotificationProvider";

const getLang = () => (window.navigator.language.startsWith("ja") ? "ja" : "en");

export default function JobEditPage() {
  const { businessDomainId, jobId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [form, setForm] = useState({ jobName: "", jobDescription: "", url: "" });
  const [fullData, setFullData] = useState(null);
  const [marketRoles, setMarketRoles] = useState([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [jobUsed, setJobUsed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [d, usedData, roles] = await Promise.all([
          fetchJob(jobId),
          fetchJobUsed(jobId).catch(() => ({ result: "NG" })),
          fetchRoles().catch(() => []),
        ]);
        setFullData(d);
        setForm({
          jobName: d.jobName || "",
          jobDescription: d.jobDescription || "",
          url: d.url || "",
        });
        setJobUsed(usedData?.result === "OK");
        setMarketRoles(Array.isArray(roles) ? roles : []);
        // Normalize roleId to string to avoid number/string type mismatch with market API id
        setSelectedRoleIds((d.roles || []).map((r) => String(r.roleId)));
      } catch (e) {
        notify.apiError(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!jobUsed && selectedRoleIds.length === 0) {
      notify.error(t("job.role.required"));
      return;
    }
    try {
      const roles = jobUsed
        ? fullData.roles
        : selectedRoleIds.map((id) => {
            const role = marketRoles.find((r) => String(r.id) === id);
            return {
              roleId: role ? role.id : id,
              roleName: role ? (getLang() === "ja" ? role.japaneseName : role.englishName) : id,
            };
          });
      await updateJob({
        ...fullData,
        ...form,
        url: form.url || null,
        roles,
      });
      notify.success(t("message.update.success"));
      navigate(`/design/${businessDomainId}/jobs/${jobId}`);
    } catch (err) {
      notify.apiError(err);
    }
  };

  if (!fullData) return null;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("job.edit.title")}
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              fullWidth
              required
              multiline
              label={t("job.name")}
              name="jobName"
              value={form.jobName}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label={t("job.description")}
              name="jobDescription"
              value={form.jobDescription}
              onChange={handleChange}
            />
          </Grid>

          <Grid size={12}>
            {jobUsed ? (
              <Box>
                <Alert severity="info" sx={{ mb: 1 }}>{t("job.roleDisabled")}</Alert>
                <TextField
                  fullWidth
                  disabled
                  label={t("job.role")}
                  value={(fullData.roles || []).map((r) => r.roleName).join(", ")}
                />
              </Box>
            ) : (
              <FormControl fullWidth>
                <InputLabel>{t("job.role")}</InputLabel>
                <Select
                  multiple
                  value={selectedRoleIds}
                  onChange={(e) => setSelectedRoleIds(e.target.value)}
                  label={t("job.role")}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((id) => {
                        const role = marketRoles.find((r) => String(r.id) === id);
                        return (
                          <Chip
                            key={id}
                            label={role ? (getLang() === "ja" ? role.japaneseName : role.englishName) : id}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {marketRoles.map((role) => (
                    <MenuItem key={role.id} value={String(role.id)}>
                      {getLang() === "ja" ? role.japaneseName : role.englishName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              type="url"
              label={t("common.url")}
              name="url"
              value={form.url || ""}
              onChange={handleChange}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">
            {t("common.save")}
          </Button>
          <Button variant="outlined" onClick={() => navigate(`/design/${businessDomainId}/jobs/${jobId}`)}>
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
